import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Accessibility, Type, Contrast, Pause, Underline, BookOpenText, AlignVerticalSpaceAround, MousePointer2, RotateCcw, X } from "lucide-react";
import { useA11y } from "@/context/A11yContext";

const TEXT_LABELS = ["Default", "Large", "Larger"];

export const AccessibilityToolbar = () => {
  const {
    textSize, cycleTextSize, highContrast, toggleContrast, pauseMotion, togglePause,
    highlightLinks, toggleLinks, readFont, toggleReadFont, spacing, toggleSpacing,
    bigCursor, toggleCursor, reset,
  } = useA11y();
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener("keydown", onKey);
    panelRef.current?.querySelector("button")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const Row = ({ icon: Icon, label, state, onClick, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      aria-pressed={typeof state === "boolean" ? state : undefined}
      className="w-full flex items-center justify-between gap-4 rounded-2xl border border-brand-line bg-white px-4 py-3 text-left hover:border-brand-blue transition-colors duration-200"
    >
      <span className="flex items-center gap-3 text-brand-ink font-semibold">
        <Icon className="h-5 w-5 text-brand-blue" strokeWidth={1.75} aria-hidden="true" /> {label}
      </span>
      <span className="text-sm font-bold text-brand-accentink">{state === true ? "On" : state === false ? "Off" : state}</span>
    </button>
  );

  return (
    <div className="fixed bottom-6 right-6 z-[100]">
      <AnimatePresence>
        {open && (
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-label="Accessibility options"
            data-testid="a11y-panel"
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="mb-3 w-[20rem] max-h-[70vh] overflow-y-auto rounded-3xl bg-white border border-brand-line shadow-2xl p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-heading font-bold text-brand-ink">Accessibility</h2>
              <button data-testid="a11y-close" onClick={() => { setOpen(false); triggerRef.current?.focus(); }} aria-label="Close accessibility options" className="grid place-items-center h-8 w-8 rounded-xl text-brand-ink2 hover:bg-brand-surface">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-2">
              <Row icon={Type} label="Text size" state={TEXT_LABELS[textSize]} onClick={cycleTextSize} testid="a11y-textsize" />
              <Row icon={BookOpenText} label="Readable font" state={readFont} onClick={toggleReadFont} testid="a11y-readfont-toggle" />
              <Row icon={AlignVerticalSpaceAround} label="Text spacing" state={spacing} onClick={toggleSpacing} testid="a11y-spacing-toggle" />
              <Row icon={Contrast} label="High contrast" state={highContrast} onClick={toggleContrast} testid="a11y-contrast-toggle" />
              <Row icon={Underline} label="Highlight links" state={highlightLinks} onClick={toggleLinks} testid="a11y-links-toggle" />
              <Row icon={MousePointer2} label="Big cursor" state={bigCursor} onClick={toggleCursor} testid="a11y-cursor-toggle" />
              <Row icon={Pause} label="Pause animations" state={pauseMotion} onClick={togglePause} testid="a11y-pause-toggle" />
            </div>
            <button data-testid="a11y-reset" onClick={reset} className="mt-3 w-full flex items-center justify-center gap-2 rounded-2xl bg-brand-surface text-brand-ink2 font-semibold py-2.5 hover:bg-brand-surface2 transition-colors duration-200">
              <RotateCcw className="h-4 w-4" /> Reset
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        ref={triggerRef}
        data-testid="a11y-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Open accessibility options"
        className="grid place-items-center h-14 w-14 rounded-full bg-brand-blue text-white shadow-xl hover:bg-brand-bluehover transition-colors duration-200"
      >
        <Accessibility className="h-7 w-7" strokeWidth={1.75} aria-hidden="true" />
      </button>
    </div>
  );
};
